// web/src/components/browser/GridView.tsx
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import type { DirEntry } from "../../worker/api";
import { FolderTile } from "./FolderTile";
import { PhotoTile } from "./PhotoTile";
import { FileTile } from "./FileTile";
import { isMediaEntry } from "./entryKind";

function humanSize(n: number | undefined): string {
  if (!n || n <= 0) return "—";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let x = n;
  while (x >= 1024 && i < units.length - 1) {
    x /= 1024;
    i++;
  }
  return `${x.toFixed(x >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

function humanAge(ts: number | undefined): string {
  if (!ts) return "—";
  const diff = (Date.now() - ts * 1000) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h`;
  return `${Math.floor(diff / 86400)} d`;
}

export function GridView({
  vaultId,
  folderPath,
  entries,
  onTapFile,
  onTapPhoto,
}: {
  vaultId: string;
  folderPath: string;
  entries: DirEntry[];
  onTapFile: (name: string) => void;
  onTapPhoto: (name: string) => void;
}) {
  const navigate = useNavigate();
  const folders = useMemo(
    () =>
      entries.filter((e) => e.kind === "dir" && !e.name.startsWith(".")),
    [entries],
  );
  const media = useMemo(
    () =>
      entries.filter(
        (e) => isMediaEntry(e) && !e.name.startsWith("."),
      ),
    [entries],
  );
  const other = useMemo(
    () =>
      entries.filter(
        (e) =>
          e.kind === "file" &&
          !isMediaEntry(e) &&
          !e.name.startsWith("."),
      ),
    [entries],
  );

  return (
    <div className="space-y-6 px-3 py-4 md:px-5">
      {folders.length > 0 && (
        <section>
          <SectionLabel>Folders · {folders.length}</SectionLabel>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-6">
            {folders.map((f) => (
              <FolderTile
                key={f.name}
                name={f.name}
                // itemCount is not surfaced by listDir today; the
                // tile renders without a count footer. A follow-up
                // milestone can extend the worker API to include
                // child counts per dir entry.
                itemCount={undefined}
                onOpen={() =>
                  navigate(
                    `/v/${vaultId}/~/${encodeURIComponent(
                      folderPath ? `${folderPath}/${f.name}` : f.name,
                    )}`,
                  )
                }
              />
            ))}
          </div>
        </section>
      )}

      {media.length > 0 && (
        <section>
          <SectionLabel>
            Media · {media.length} items ·{" "}
            {humanSize(media.reduce((sum, e) => sum + (e.size ?? 0), 0))}
          </SectionLabel>
          <div className="grid grid-cols-3 gap-1.5 md:grid-cols-6">
            {media.map((m) => (
              <PhotoTile
                key={m.name}
                folderPath={`/${folderPath}`.replace(/\/+/g, "/")}
                fileName={m.name}
                mime={m.mime}
                onTap={() => onTapPhoto(m.name)}
              />
            ))}
          </div>
        </section>
      )}

      {other.length > 0 && (
        <section>
          <SectionLabel>Files · {other.length}</SectionLabel>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-6">
            {other.map((f) => (
              <FileTile
                key={f.name}
                name={f.name}
                sizeLabel={humanSize(f.size)}
                modifiedLabel={humanAge(f.modified)}
                onTap={() => onTapFile(f.name)}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-2 text-[10px] font-medium uppercase tracking-[0.14em] text-text-dim">
      {children}
    </div>
  );
}
